import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { GetSerieDetailResponse, GetSerieEconomicsResponse } from '@/api/generated/schemas';
import { cn } from '@/shared/lib/utils';
import { MiniProgressBar, Skeleton, type MiniProgressTone } from '@/shared/ui';
import { formatInteger, formatOneDecimal } from '../serieFormat';
import { toSerieTransportKey } from '../../internal/serieLayers';

interface SerieStatsZoneProps {
  detail: GetSerieDetailResponse;
  economics: GetSerieEconomicsResponse | undefined;
  isEconomicsLoading: boolean;
}

// Buňka pruhu: popisek (caps) nad hodnotou, svisle vystředěné, hairline mezery řeší padding.
const CELL = 'flex min-w-0 flex-col justify-center px-phx-6 first:pl-phx-3 last:pr-phx-3';
const KEY =
  'text-phx-text-muted text-phx-3xs font-semibold uppercase leading-[1.5] tracking-[0.06em] whitespace-nowrap';
const VALUE =
  'text-phx-text text-phx-lg font-semibold leading-[1.25] tabular-nums whitespace-nowrap';
const UNIT = 'text-phx-text-muted ml-phx-1 text-phx-2xs font-normal';

function StatCell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={CELL}>
      <span className={KEY}>{label}</span>
      {children}
    </div>
  );
}

function reservationTone(pct: number): MiniProgressTone {
  if (pct >= 85) return 'full';
  if (pct < 50) return 'low';
  return 'normal';
}

/**
 * Horní pruh statistik seriálu (schválená varianta „band“ – full-bleed pruh dle design systému). Ekonomické
 * buňky (Rezervace / Nejnižší cena / Průměrná marže) ukazují skeleton, dokud nedoběhne samostatný ekonomický
 * request. Pruh se roztáhne přes celou šířku obsahu (`-mx-4 -mt-4` ruší padding stránky) a sedí pod hlavičkou.
 */
export function SerieStatsZone({ detail, economics, isEconomicsLoading }: SerieStatsZoneProps) {
  const { t } = useTranslation('venua-planner');
  const loadingEconomics = isEconomicsLoading || !economics;

  const reservationPct =
    economics && economics.reservationsCapacity > 0
      ? Math.round((economics.reservationsBooked / economics.reservationsCapacity) * 100)
      : 0;

  return (
    <div className="bg-phx-surface-2 gap-y-phx-4 px-phx-6 flex flex-wrap items-stretch py-[11px]">
      <StatCell label={t('serieDetail.stats.code')}>
        <span className={cn(VALUE, 'text-phx-sm font-mono')}>{detail.productCode}</span>
      </StatCell>
      <StatCell label={t('serieDetail.stats.transport')}>
        <span className={VALUE}>
          {t(`serie.transport.${toSerieTransportKey(detail.transportType)}`)}
        </span>
      </StatCell>
      <StatCell label={t('serieDetail.stats.capacity')}>
        <span className={VALUE}>
          {detail.capacityPerTerm}
          <span className={UNIT}>{t('serieDetail.stats.capacityUnit')}</span>
        </span>
      </StatCell>
      <StatCell label={t('serieDetail.stats.reservation')}>
        {loadingEconomics ? (
          <Skeleton width={72} />
        ) : (
          <>
            <span className={VALUE}>
              {economics.reservationsBooked}
              <span className={UNIT}>
                {t('serieDetail.stats.reservationSecondary', {
                  capacity: economics.reservationsCapacity,
                  percent: reservationPct,
                })}
              </span>
            </span>
            <MiniProgressBar
              value={economics.reservationsBooked}
              max={economics.reservationsCapacity}
              width={58}
              tone={reservationTone(reservationPct)}
              className="mt-phx-2"
            />
          </>
        )}
      </StatCell>
      <StatCell label={t('serieDetail.stats.price')}>
        {loadingEconomics ? (
          <Skeleton width={54} />
        ) : (
          <span className={VALUE}>
            {formatInteger(economics.priceFrom)}
            <span className={UNIT}>{economics.currency}</span>
          </span>
        )}
      </StatCell>
      <StatCell label={t('serieDetail.stats.margin')}>
        {loadingEconomics ? (
          <Skeleton width={40} />
        ) : (
          <span className={VALUE}>
            {formatOneDecimal(economics.marginPercent)}
            <span className={UNIT}>%</span>
          </span>
        )}
      </StatCell>
      <StatCell label={t('serieDetail.stats.terms')}>
        <span className={VALUE}>{detail.termsCount}</span>
      </StatCell>
    </div>
  );
}
